import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ChevronLeft, MailCheck } from "lucide-react";
import api from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      // Backend is enumeration-safe — always returns success regardless of
      // whether the email exists. We show the same success screen.
      setSent(true);
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F0FAFA] p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-10 w-10 rounded-md bg-white border border-slate-200 flex items-center justify-center overflow-hidden">
            <img src="/logo.png" alt="Rabah Concept" className="h-9 w-9 object-contain" />
          </div>
          <p className="font-heading font-bold text-xl text-slate-900">Rabah Concept</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="forgot-password-panel">
          {!sent ? (
            <>
              <Link to="/login" className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-3" data-testid="forgot-back-login">
                <ChevronLeft className="h-4 w-4" /> Back to sign in
              </Link>
              <h2 className="font-heading text-2xl font-bold text-slate-900">Reset your password</h2>
              <p className="mt-1 text-sm text-slate-600">Enter your work email and we&apos;ll send a link to set a new password. The link is valid for 1 hour.</p>
              <form onSubmit={submit} className="mt-5 space-y-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@yourcompany.co.uk"
                    data-testid="forgot-email-input"
                    className="mt-1 w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-[#0D7377] focus:ring-2 focus:ring-[#0D7377]/20 outline-none bg-white"
                  />
                </div>
                {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
                <button type="submit" disabled={busy} data-testid="forgot-submit-button" className="w-full inline-flex items-center justify-center gap-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white font-semibold py-2.5 rounded-md disabled:opacity-60 min-h-[44px]">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send reset link
                </button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-sent-panel">
              <div className="flex items-center gap-2 text-emerald-700 mb-3">
                <MailCheck className="h-6 w-6" />
                <h2 className="font-heading text-xl font-bold">Check your inbox</h2>
              </div>
              <p className="text-sm text-slate-700">If an account exists for <strong>{email}</strong>, a password reset link has been sent. The link expires in 1 hour.</p>
              <p className="text-xs text-slate-500 mt-3">Didn&apos;t get an email? Check your spam folder or contact your Registered Manager.</p>
              <Link to="/login" className="mt-5 inline-block text-sm font-semibold text-[#0D7377] hover:text-[#0A5C5F]" data-testid="forgot-return-login">
                Return to sign in →
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
